import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight, Mail } from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";
import Header from "@/components/landing/Header";

const API_URL = process.env.REACT_APP_API_URL || "";
const APP_URL = process.env.REACT_APP_APP_URL || "https://app.ekklesiaonline.app";

export default function SubscripcionConfirmadaPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [data, setData] = useState(null);

  // Limpiar el carrito al confirmar la suscripción
  useEffect(() => {
    try { sessionStorage.removeItem('ekklesia_cart'); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setStatus("success"); // mostrar página genérica si no hay session_id
      return;
    }
    // Validar formato de session_id antes de enviar al servidor (Stripe: cs_live_... / cs_test_...)
    if (!/^cs_(live|test)_[a-zA-Z0-9]+$/.test(sessionId)) {
      setStatus("success"); // mostrar página genérica sin revelar el error
      return;
    }
    axios
      .get(`${API_URL}/public/billing/session?session_id=${encodeURIComponent(sessionId)}`)
      .then((res) => {
        if (res.data?.ok) {
          setData(res.data);
        }
        setStatus("success");
      })
      .catch(() => {
        setStatus("success"); // aún mostrar confirmación aunque no podamos leer la sesión
      });
  }, [sessionId]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-white">
      <Seo
        title="Suscripción confirmada"
        description="Tu pago se ha procesado correctamente. Pronto recibirás los siguientes pasos para acceder a Ekklesia."
        path="/subscripcion-confirmada"
        noIndex
      />
      <Header />

      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-16 pt-28 sm:px-6">
        {status === "loading" ? (
          <Loader2 className="h-10 w-10 animate-spin text-violet-fuerte" />
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md rounded-3xl border border-violet-light bg-white p-8 text-center shadow-[0_20px_60px_-20px_rgba(74,58,138,0.15)]"
          >
            {/* Icon */}
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-[#EDF8F3]">
              <CheckCircle2
                className="h-10 w-10 text-[#2D8A5E]"
                strokeWidth={1.5}
              />
            </div>

            {/* Heading */}
            <h1 className="font-display text-2xl font-black tracking-tight text-ink sm:text-3xl">
              ¡Suscripción activada!
            </h1>

            <p className="mt-3 text-base leading-relaxed text-ink-2">
              {data?.email ? (
                <>
                  Enviamos las instrucciones de acceso a{" "}
                  <span className="font-semibold text-violet-fuerte">
                    {data.email}
                  </span>
                  . Revisa también la carpeta de spam.
                </>
              ) : (
                <>
                  Revisa tu correo electrónico. Te hemos enviado las
                  instrucciones para acceder a tu cuenta.
                </>
              )}
            </p>

            {/* Plan info */}
            {data?.plan && (
              <div className="mt-5 rounded-xl border border-violet-light bg-[#FAFAFE] px-5 py-4">
                <p className="text-sm text-ink-3">Plan contratado</p>
                <p className="mt-1 font-display text-lg font-bold text-ink">
                  {data.plan}
                </p>
                {data.amount_formatted && (
                  <p className="mt-0.5 text-sm font-semibold text-violet-fuerte">
                    {data.amount_formatted}
                  </p>
                )}
              </div>
            )}

            {/* Email hint */}
            <div className="mt-5 flex items-start gap-3 rounded-xl border border-violet-light bg-[#FAFAFE] px-4 py-3 text-left">
              <Mail
                className="mt-0.5 h-4 w-4 shrink-0 text-ink-3"
                strokeWidth={1.75}
              />
              <p className="text-[13px] leading-relaxed text-ink-3">
                Si no encuentras el correo, escríbenos a{" "}
                <a
                  href="mailto:soporte@ekklesiaonline.app"
                  className="font-semibold text-violet-fuerte hover:underline"
                >
                  soporte@ekklesiaonline.app
                </a>{" "}
                y te ayudamos en minutos.
              </p>
            </div>

            {/* CTA */}
            <div className="mt-7 flex flex-col gap-3">
              <a
                href={APP_URL}
                className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-violet-fuerte text-[16px] font-semibold text-white shadow-[0_4px_20px_-4px_rgba(74,58,138,0.45)] transition-all duration-200 hover:bg-violet-oscuro hover:shadow-[0_6px_28px_-4px_rgba(74,58,138,0.55)] hover:-translate-y-px"
              >
                Ir a la aplicación
                <ArrowRight className="h-5 w-5" strokeWidth={2} />
              </a>
              <Link
                to="/"
                className="text-sm font-semibold text-ink-3 transition-colors hover:text-violet-fuerte"
              >
                Volver al inicio
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
